import {BaseApis}                 from "@Common/apis/next/base-apis";
import {BaseShippingApis}         from "@Common/apis/next/base-shipping-apis";
import {MailingApis}              from "@Common/apis/next/mailing/mailing-apis";
import {nextInformedDeliveryApis} from "@Common/apis/next/next-informed-delivery-apis";
import {nextNetworkingApis}       from "@Common/apis/next/next-networking-apis";
import {NextSocialApis}           from "@Common/apis/next/next-social-apis";
import {SampleAddressApis}        from "@Common/apis/next/sample-address-apis";
import {Types}                    from "@Common/types";
import {buildUrlQuery}            from "@Common/utils/build-url-query";
import {chain}                    from "@Common/utils/fs";
import {equalDeep, mapValues,
        omit, values, diffKeys}   from "@Common/utils/objects";

import {AGENT_LEVEL} from "../../../routes/common/logic/user-level";

import {CartApis}         from "./cart/cart-apis";
import {remapPricingData} from "./remap-pricing-data";

const fetcher = (() => {
    const readJsonResponse = async (res, {onUnauthorized}) => {
        if (res.status === 401) {
            onUnauthorized && onUnauthorized();
            return null;
        } else if (res.status === 204) {
            return null;
        }
        try {
            const json = await res.json();
            if(!json) {
                throw res;
            }
            return json;
        } catch {
            throw res;
        }
    };

    const fetchJson = async (url, {body=undefined, onUnauthorized, options, returnJson=true}) => {
        const res = await fetch(url, {
            credentials: "include",
            ...body != null && {
                body: JSON.stringify(body),
                headers: {
                    "content-type": "application/json",
                },
            },
            ...options,
        });
        return returnJson ? readJsonResponse(res, {onUnauthorized}) : res;
    };
    const fetchMultipart = async (url, {body=undefined, onUnauthorized, options}) => {

        const res = await fetch(url, {
            credentials: "include",
            ...body != null && {
                body: body instanceof FormData ? body : (() => {
                    const formData  = new FormData();

                    for(const name in body) {
                        formData.append(name, body[name]);
                    }
                    return formData;
                })(),
            },
            ...options,
        });
        return await readJsonResponse(res, {onUnauthorized});
    };
    return {
        getJson: (url, {onUnauthorized, options=undefined, returnJson=true}) => fetchJson(url, {
            options,
            onUnauthorized,
            returnJson,
        }),
        postJson: (url, data, {onUnauthorized, returnJson=true, ...options}) => fetchJson(url, {
            body: data,
            options: {
                method: "POST",
                ...options,
            },
            onUnauthorized,
            returnJson,
        }),
        postMultipart: (url, data, {onUnauthorized}) => fetchMultipart(url, {
            body: data,
            options: {
                method: "POST"
            },
            onUnauthorized,
        }),
        deleteJson: (url, data, {onUnauthorized}) => fetchJson(url, {
            body: data,
            options: {
                method: "DELETE"
            },
            onUnauthorized,
        }),

        // post using XMLHttpRequest - useful if need progress events
        postMultipartXHR: async (url, data, {onUploadProgress, onDownloadProgress, onUnauthorized, chunkedUploadSizeAlreadySent, chunkedUploadSizeTotal}) => {

            // add data to FormData object
            let body = new FormData();
            for(const name in data) {
                body.append(name, data[name]);
            }

            // post via XHR
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            const response = await new Promise((resolve) => {

                // add handler for upload progress if function provided
                if (onUploadProgress) {
                    xhr.upload.addEventListener("progress", (event) => {
                        if (event.lengthComputable) {
                            const sizeLoaded = event.loaded + (chunkedUploadSizeAlreadySent || 0);
                            const sizeTotal = chunkedUploadSizeTotal || event.total;
                            onUploadProgress(Math.min(Math.round(sizeLoaded / sizeTotal * 100), 100));
                        }
                    });
                }

                // add handler for download progress if function provided
                if (onDownloadProgress) {
                    xhr.addEventListener("progress", (event) => {
                        if (event.lengthComputable) {
                            onDownloadProgress(Math.min(Math.round(event.loaded / event.total * 100), 100));
                        }
                    });
                }

                // resolve promise after completion
                xhr.addEventListener("loadend", () => {
                    if(onUnauthorized && xhr.status === 401) onUnauthorized();
                    resolve(JSON.parse(xhr.responseText));
                });

                // post to URL
                xhr.open("POST", url, true);
                xhr.send(body);

            });

            return response;

        },
    };
})();

export const createNextApis = ({onUnauthorized}) => {

    const authFetcher = {
        getJson : (url, options=undefined, returnJson=true) => fetcher.getJson(url, {onUnauthorized, options, returnJson}),
        postJson: (url, data=null, options=null) => fetcher.postJson(url, data, {onUnauthorized, ...options}),
        postMultipart: (url, data=null, onUploadProgress=null, onDownloadProgress=null, chunkedUploadSizeAlreadySent=0, chunkedUploadSizeTotal=0):Promise<any> => (
            (onUploadProgress || onDownloadProgress) ? (
                fetcher.postMultipartXHR(url, data, {onUploadProgress, onDownloadProgress, onUnauthorized, chunkedUploadSizeAlreadySent, chunkedUploadSizeTotal})
            ) : (
                fetcher.postMultipart(url, data, {onUnauthorized})
            )
        ),
        deleteJson: (url, data=null) => fetcher.deleteJson(url, data, {onUnauthorized}),
    };

    // uploads a file in chunks and returns a file token that can be used as a reference to the file in services that
    // support it
    const sendChunkedUpload = async (file, onUploadProgress, onDownloadProgress):Promise<Types.Apis.Payloads.Next.Uploads.UploadPartResponse> => {

        const originalFileName = file.name;

        // read the file as a base64 string
        let data:string = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = async () => {
                let content = reader.result.toString();
                content = content.replace(/^data:(.*,)?/, '');
                if ((content.length % 4) > 0) {
                    content += '='.repeat(4 - (content.length % 4));
                }
                resolve(content);
            };
            reader.readAsDataURL(file); // base64 data
        });
        if (!data) return {};

        // set chunk size
        // when uploading in chunks of string post data, the progress events appear to only file when the
        // chunk has completed upload, so keep a smaller chunk size to get a smoother transition, but there
        // is significant overhead with each packet that will slow down the complete upload, so don't make
        // it too small (ex with 5MB file: 100K = 52s, 500K = 18s, 1M = 14s, 10M = 12s
        const chunkSize = 1000000;

        // slice the data into chunks and upload each chunk
        let fileToken = "";
        let upload:Types.Apis.Payloads.Next.Uploads.UploadPartResponse = {};
        const totalSize = data.length;
        let currentPosition = 0;
        while (data) {

            const chunk = data.slice(0, chunkSize);
            data = data.slice(chunkSize);

            upload = await authFetcher.postMultipart(
                `/next-api/uploads/upload-part`,
                {
                    originalFileName,
                    fileToken,
                    data: chunk,
                    dataStartIndex: currentPosition
                },
                onUploadProgress,
                onDownloadProgress,
                currentPosition,
                totalSize
            );

            if (!upload.fileToken) return upload;
            if (!fileToken) fileToken = upload.fileToken;
            currentPosition += chunkSize;

        }

        // return the file token
        return upload;

    };

    return ({
        ...BaseApis({fetcher: authFetcher}),

        validateXpressoToken: ({xpressoToken}) => {
            return authFetcher.postJson("/next-api/authenticate/token", {"token": "" + xpressoToken});
        },
        getCatSubStyle: () => {
            const url = "/next-api/catalog/catSubStyle/my";
            return authFetcher.getJson(url);
        },
        getStyleTemplate: (subIds) => authFetcher.getJson(
            `/next-api/template/templatesIds/style/${subIds.join(",")}` +
            ["listingId", "listingRequestType"]
                .map((k) => {
                    const value = sessionStorage.getItem(k);
                    return value && "/" + encodeURIComponent(value);
                })
                .filter(v => v)
                .join("")
        ),
        getSuggestedSizeTemplate: (width, height) => authFetcher.getJson(
            `/next-api/template/suggestedTemplatesIds/size/${width}/${height}`
        ),
        getRotatedSizeTemplate: (width, height) => authFetcher.getJson(
            `/next-api/template/templatesIds/rotatedSize/${width}/${height}`
        ),
        getSizeTemplate: (width, height) => authFetcher.getJson(
            `/next-api/template/templatesIds/size/${width}/${height}`
        ),
        getTemplateBack: (templateId): Promise<Types.Apis.Payloads.Next.TemplateBack.GetTemplates.Response> => (
            authFetcher.getJson(`/next-api/template-back/templates/${templateId}`)
        ),

        getPricing: async (templateId) => {
            const data = await authFetcher.getJson(`/next-api/template-back/pricing/${templateId}`);
            return remapPricingData(data);
        },

        searchTemplates: ({query, skip, limit}) => {
            query = query.replace(/[^0-9a-z]+/i, " ").toLowerCase();
            return authFetcher.getJson(`/next-api/template/search/${encodeURIComponent(query)}?maxResults=${encodeURIComponent(limit)}&offset=${encodeURIComponent(skip)}`)
        },
        getThumbnailData: (orderId, side, idComponent) => authFetcher.getJson(`/next-api/template/get-thumbnail-data/${orderId}/${side}${idComponent ? `/${idComponent}` : ""}`),
        isLastProofAvailable: (orderId) => authFetcher.getJson(`/next-api/proofing-stats/is-last-proof-available/${orderId}`),
        lastProofError: (orderId) => authFetcher.getJson(`/next-api/proofing-stats/last-proof-error/${orderId}`),
        initiateProof: (orderId, idComponent) => authFetcher.getJson(`/next-api/proofing-stats/initiate-proof/${orderId}${idComponent ? `/${idComponent}` : ""}`),
        initiateProofing: (orderId, idComponent, ignoreProofStats=false) => authFetcher.getJson(
            `/next-api/proofing/initiate-proof/${orderId}${idComponent ? `/${idComponent}` : "/0"}${ignoreProofStats ? `/1` : ""}`
        ),
        loadMenu: () => authFetcher.getJson("/next-api/menusvc/menu/all"),
        loadAccountMenu: () => authFetcher.getJson("/next-api/menusvc/menu/account"),
        getSystemConstants: () => authFetcher.getJson("/next-api/system-constants/system-constants"),
        getThemeScheme: (companyXdocsUrl=null) => authFetcher.getJson("/next-api/scheme" + (companyXdocsUrl ? `/${companyXdocsUrl}` : '')),
        saveXpressoThemeScheme: (
            scheme:Types.Apis.Payloads.Next.Scheme.PostXpressoScheme.Request
        ): Promise<Types.Apis.Payloads.Next.Scheme.PostXpressoScheme.Response> =>
        {
            const formData = new FormData();
            for (let key in scheme) {
                scheme[key] instanceof File ? (
                    formData.set(key, scheme[key], scheme[key].name)
                ) : (
                    formData.set(key, scheme[key])
                )
            }
            if ("deleteHeaderBanner" in scheme) {
                formData.set("deleteHeaderBanner", scheme.deleteHeaderBanner ? "1" : "0");
            }
            if ("deleteHeaderLogo" in scheme) {
                formData.set("deleteHeaderLogo", scheme.deleteHeaderLogo ? "1" : "0");
            }
            return authFetcher.postMultipart("/next-api/scheme", formData);
        },
        getUpdatedLiveChatConfig: (firstName,lastName) => authFetcher.getJson(`/next-api/livechat/${encodeURIComponent(firstName)}/${encodeURIComponent(lastName)}`),
        getOrdersInCart: (fetchOptions) => authFetcher.getJson("/next-api/cart/orders", fetchOptions),

        // Should always have value (list length > 0)
        getCustomerAgreement: (groupName='checkout') => authFetcher.getJson(`/next-api/customerAgreement/get/${groupName}`),


        getNotifications: () => authFetcher.getJson("/next-api/notifications/getMessages"),
        getPopupModals: (targetUrl:string=null, inLegacy:boolean=null, inXpresso:boolean=true): Promise<Types.Apis.Payloads.Next.GetPopupModalsResponse> => {
            const params = {
                ...(targetUrl && {targetUrl}),
                ...(inLegacy && {inLegacy: Number(inLegacy)}),
                ...(inXpresso && {inXpresso: Number(inXpresso)}),
            };
            return authFetcher.getJson(`/next-api/notifications/popupModals${buildUrlQuery(params)}`);
        },
        permanentlyDismissPopup: async (idModal:number) => await authFetcher.postJson("/next-api/notifications/permanentlyDismissPopup", idModal),

        templateDownloadLog: async (idTemplate, downloadLocation ) => await authFetcher.getJson(`/next/ajax/template_download_ajax.php?action=log&id_template=${idTemplate}&download_location=${encodeURIComponent(downloadLocation)}`),

        agent: {
            getAllUsers: async (params:Types.Apis.Payloads.Next.Agent.GetAllUsers.Request=null):Promise<Types.Apis.Payloads.Next.Agent.GetAllUsers.Response> =>
            {
                const query = buildUrlQuery({...params, ...(params?.inactive != null && {inactive: Number(params.inactive)})});
                return authFetcher.getJson( `/next-api/agent/all-users${query}` );
            },
        },

        branch: {
            createBranch: async ({createFields}:{createFields:Types.Apis.Payloads.Next.Branch.UpdateBasicInfo.Request}) => (
                await authFetcher.postJson("/next-api/branch/createBasicInfoBranch", createFields)
            ),
            updateBasicInfo: (fields:Types.Apis.Payloads.Next.Branch.UpdateBasicInfo.Request): Promise<void> => (
                authFetcher.postJson("/next-api/branch/updateBasicInfo", fields, {returnJson: false})
                    .then((res:Response) => {
                        if (res.status >= 400)
                            return Promise.reject(res);
                    })
            )
        },

        contactUs: {
            submitSupportRequest: (request:Types.Apis.Payloads.Next.ContactUs.PostContactUs.Request): Promise<any> => (
                authFetcher.postJson("/next-api/contactus/post", request)
            )
        },

        session: {

            setSessionListing: async (record) => authFetcher.postJson(`/next-api/session/set_listing`, {record}),

            getExistingOrderSessionInfo: async () => authFetcher.getJson(`/next-api/session/data`),

            // No longer used
            // getEmulation: async () => {
            //     const emu = await authFetcher.postJson("/next-api/emulate/user", {});
            //     if (!emu.length) {
            //         return {type: "none"};
            //     } else if (emu.length === 1) {
            //         const branch = emu.find((r) => r.idBranch);
            //         return {
            //             type: "branch",
            //             value: {
            //                 branchId: branch.idBranch,
            //             },
            //             branch,
            //         };
            //     } else if (emu.find((r) => r.idAgent && r.idAgent !== "Branch Order")) {
            //         const agent = emu.find((r) => r.idAgent);
            //         const branch = emu.find((r) => r.idBranch);
            //         return {
            //             type: "agent",
            //             value: {
            //                 agentId : agent.idAgent,
            //                 branchId: branch.idBranch,
            //             },
            //             agent,
            //             branch,
            //         };
            //     } else if (emu.find((r) => r.idAgent && r.idAgent === "Branch Order")) {
            //         const branch = emu.find((r) => r.idBranch);
            //         return {
            //             type: "branch_order",
            //             value: {
            //                 branchId: branch.idBranch,
            //             },
            //             branch,
            //         };
            //     } else {
            //         throw new Error("Unknown '/next-api/emulate/user' response: " + JSON.stringify(emu));
            //     }
            // },

            getUserPath: async () => await authFetcher.postJson("/next-api/emulate/user", {}),

            emulateAgent: async ({branchId, agentId}) => await authFetcher.postJson("/next-api/emulate/user", {
                idBranch: branchId,
                idAgent: agentId,
            }),
            emulateBranch: async ({branchId}) => await authFetcher.postJson("/next-api/emulate/user", {
                idBranch: branchId,
            }),
            emulateBranchOrder: async ({branchId}) => await authFetcher.postJson("/next-api/emulate/user", {
                idBranch: branchId,
                idAgent: "Branch Order",
            }),
            clearEmulation: async () => await authFetcher.postJson("/next-api/emulate/user", {"idBranch": "", "idAgent": ""}),
            clearSessionOrder: async () => {
                return await authFetcher.postJson("/next-api/session/clear/order");
            },
            getUserLevels: async (): Promise<Types.Apis.Payloads.Next.Session.UserLevel.Response> => {
                // integer userLevel: 3=company level, 4=branch level, 5=agent level
                // boolean internalUser
                return await authFetcher.getJson(`/next-api/session/user_level`);
            },
            getAgent: async ({agentId=null} = {}) => await authFetcher.getJson("/next-api/agent/get-agent" + (agentId ? `?idAgent=${agentId}` : '')),
            getRegion: async (): Promise<Types.Apis.Payloads.Next.Session.GetRegionResponse> =>
                await authFetcher.getJson("/next-api/region/get-region"),
            getBranch: async ({officeId=null} = {}): Promise<Types.Apis.Payloads.Next.Session.GetBranchResponse> =>
                await authFetcher.getJson("/next-api/branch/get-branch" + (officeId ? `?idBranch=${officeId}` : '')),
            getCompany: async () => await authFetcher.getJson("/next-api/company/get-company"),
            getMessages: async () => await authFetcher.getJson("/next-api/messages/get-messages"),
            getSuggestedComments: async ({request}) => await authFetcher.postJson("/next-api/suggestedComments/getSuggestedComments", request),

            createAgent: async ({createFields}) => await authFetcher.postJson("/next-api/agent/createAgentBasicInfo", createFields),
            updateAgent: async ({updateFields}) => await authFetcher.postJson("/next-api/agent/updateBasicInfo", updateFields),
            registerGuestAgent: async({createFields}) => await authFetcher.postJson("/next-api/agent/registerGuestAgent", createFields),
            updateBranch: async ({updateFields}) => await authFetcher.postJson("/next-api/branch/updateBasicInfo", updateFields),
            updateCompany: async ({updateFields}) => await authFetcher.postJson("/next-api/company/updateBasicInfo", updateFields),

            validateActiveEntity: async (
                entity:Types.Apis.Payloads.Next.Session.ValidateActiveEntity.Request,
                abortSignal?: AbortSignal,
            ): Promise<Types.Apis.Payloads.Next.Session.ValidateActiveEntity.Response> => (
                await authFetcher.postJson("/next-api/session/validateActiveEntity", entity, {signal: abortSignal})
            ),
        },

        item: {
            createItem: async (item) => authFetcher.postJson(`/next-api/item/create`, item),
            updateItem: async (item) => authFetcher.postJson(`/next-api/item/update`, item),
            cloneItem: async ({cloneItemJson}) => authFetcher.postJson(`/next-api/item/clone`, cloneItemJson),
            cancelItem: async ({cancelItemJson}) => authFetcher.postJson(`/next-api/item/cancel_item`, cancelItemJson),
            getItem: async (itemId): Promise<Types.Apis.Payloads.Next.Item.Get.Response> => (
                authFetcher.getJson(`/next-api/item/get/${itemId}`)
            ),
            getItemProofs: async (itemId) => authFetcher.getJson(`/next-api/item/getproofs/${itemId}`),
            deleteItem: async (itemId) => authFetcher.postJson(`/next-api/item/delete_item`, {itemId:""+itemId}),
            getPending: async () => authFetcher.getJson(`/next-api/item/pending`),
            getHistory: async ({searchJson}): Promise<Types.Apis.Payloads.Next.Item.GetHistory.Response> => (
                authFetcher.postJson(`/next-api/item/history`, searchJson)
                // fix response types c.c
                .then(({carts, hasMore}:Types.Apis.Payloads.Next.Item.GetHistory.RawResponse):Types.Apis.Payloads.Next.Item.GetHistory.Response => ({
                    hasMore,
                    carts: mapValues(carts, (cart) => ({
                        ...cart,
                        cartId : parseInt(cart.cartId),
                        items  : cart.items.map((item) => ({
                            ...item,
                            allowEditOffice : item.allowEditOffice === "1",
                            allowEditUser   : item.allowEditUser   === "1",
                            itemId          : parseInt(item.itemId),
                            selections      : {
                                ...item.selections,
                                mailingListIds : item.selections.mailingListIds?.map(mailingList => ({
                                    ...mailingList,
                                    id        : parseInt(mailingList.id),
                                    unitPrice : parseFloat(mailingList.unitPrice as string),
                                })),
                                quantity       : parseFloat(item.selections.quantity as string)
                            },
                        }))
                    }))
                }))
            ),
            getScheduled: async () => authFetcher.getJson(`/next-api/item/scheduled`),
            updateScheduledDate: async ({itemId,scheduledDate}) => authFetcher.postJson(`/next-api/item/updateScheduledDate`, {"itemId":""+itemId,"scheduledDate":""+scheduledDate}),
            getTracing: async ({itemId}) => authFetcher.getJson(`/next-api/item/getTracing/${itemId}`),
            getTracingDownload: async ({itemId}) => authFetcher.getJson(`/next-api/item/getTracingDownload/${itemId}`, undefined, false),
            createProofApprovalRequest: async (itemId) => authFetcher.postJson(`/next-api/orderApproval/createProofApprovalRequest`, {"orderId":""+itemId}),
        },

        event: {
            cloneEvent: async ({eventId}) => authFetcher.getJson(`/next-api/item/cloneEvent/${eventId}`),
        },

        order: {
            saveImageAssetReferences: (orderId, imageAssetReferences, idComponent=null) => authFetcher.postJson(`/next-api/order/image-assets/${orderId}${idComponent ? `/${idComponent}` : ""}`, imageAssetReferences),
            getContentFields: ($idOrder, frontAndBackTemplateIds, idComponent=null) => authFetcher.postJson(`/next-api/order/content-fields/${$idOrder}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            getSpecFields: ($idOrder, frontAndBackTemplateIds, idComponent=null) => authFetcher.postJson(`/next-api/order/spec-fields/${$idOrder}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            getTemplateSpecFields: (frontAndBackTemplateIds) => authFetcher.postJson(`/next-api/order/template-spec-fields/`, frontAndBackTemplateIds),
            getColorSet: (frontTemplateId, backTemplateId) => authFetcher.getJson(`/next-api/colorSets/getColorSet/${backTemplateId}/${frontTemplateId}`),
        },

        OrderManager: {
            getOrderData: (idOrder, idComponent=null) => authFetcher.getJson(`/next-api/orderManager/getOrderData/${idOrder}${idComponent ? `/${idComponent}` : ""}`),
            updateOrderData: (idOrder, orderData, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/updateOrderData/${idOrder}${idComponent ? `/${idComponent}` : ""}`, orderData),
            updateBranchAgentOrderData: (idOrder, idCompany, idBranch, idAgent, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/updateBranchAgentOrderData/${idOrder}/${idCompany}/${idBranch}/${idAgent}${idComponent ? `/${idComponent}` : ""}`),
            updateAgentHeadshots: (idOrder, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/updateAgentHeadshots/${idOrder}${idComponent ? `/${idComponent}` : ""}`),
            createOrderDataEntry: (idOrder, orderData, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/createOrderDataEntry/${idOrder}${idComponent ? `/${idComponent}` : ""}`, orderData),
            createOrderImageEntries: (idOrder, frontAndBackTemplateIds, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/createOrderImageEntries/${idOrder}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            getOrderImageFields: (idOrder, idComponent=null) => authFetcher.getJson(`/next-api/orderManager/getOrderImageFields/${idOrder}${idComponent ? `/${idComponent}` : ""}`),
            getTemplateDefiImages: (idTemplate, imageType=null) => authFetcher.getJson(`/next-api/orderManager/getTemplateDefiImages/${idTemplate}${imageType ? `/${imageType}` : ""}`),
            getColorFields: (idOrder, idComponent=null) => authFetcher.getJson(`/next-api/orderManager/getColorFields/${idOrder}${idComponent ? `/${idComponent}` : ""}`),
            updateOrderImageFields: (idOrder, imageFields, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/updateOrderImageFields/${idOrder}${idComponent ? `/${idComponent}` : ""}`, imageFields),
            checkRequiredTextFields: (idOrder, frontAndBackTemplateIds, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/checkRequiredTextFields/${idOrder}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            checkEmailValidationTextFields: (idOrder, frontAndBackTemplateIds, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/checkEmailValidationTextFields/${idOrder}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            checkRequiredImageFields: (idOrder, frontAndBackTemplateIds, isAgent=1, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/checkRequiredImageFields/${idOrder}/${isAgent}${idComponent ? `/${idComponent}` : ""}`, frontAndBackTemplateIds),
            checkImageFieldsResolution: (idOrder, qualityCheckParams, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/checkImageFieldsResolution/${idOrder}${idComponent ? `/${idComponent}` : ""}`, qualityCheckParams),
            applyListingData: (idOrder, listing, idComponent=null) => authFetcher.postJson(`/next-api/orderManager/apply-listing-data/${idOrder}${idComponent ? `/${idComponent}` : ""}`, listing),
            getOrderColumn: (idOrder, columnName) => authFetcher.getJson(`/next-api/orderManager/getOrderColumn/${idOrder}/${columnName}`),
        },

        legacyOrder: {
            getPreviewUrl: (orderId) => authFetcher.getJson(`/next/web_services/get_production_pdf.php?id_order=${encodeURIComponent(orderId)}&is_pending_transaction=1&is_approval=1`),
        },

        guideFiles: {
            getGuideFiles: (productIds: number[] = []): Promise<Blob> =>
                fetch(`/next/web_services/get_guide_files.php`, {
                    method: 'GET',
                    headers: {
                        'product_ids': JSON.stringify(productIds),
                    },
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch guide files: ${response.statusText}`);
                    }
                    return response.blob();
                }),
            listGuideFiles: () => authFetcher.getJson(`/next/web_services/list_guide_files.php`),
        },

        gallery: {

            getAllowedPhotoGalleryTabs: (companyAndTemplateInfo) => authFetcher.postJson(`/next-api/gallery/allowedPhotoGalleryTabs`, companyAndTemplateInfo),

            propertyPhotos: {
                getFolders: async (): Promise<Types.Apis.Payloads.Next.Gallery.PropertyPhotosGetFoldersResponse> =>
                    authFetcher.getJson(`/next-api/gallery/property-photos/folders`),
                getPhotos: async (folderId:number): Promise<Types.Apis.Payloads.Next.Gallery.PropertyPhotosGetPhotosResponse> =>
                    authFetcher.getJson(`/next-api/gallery/property-photos/folders/${folderId}`),
            },

            headshots: {
                asCutoutPro: (params:Types.Apis.Payloads.Next.Gallery.HeadshotsAsCutoutProRequest): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsAsCutoutProResponse> =>
                    authFetcher.getJson(`/next-api/gallery/headshots/${params.id}/asCutoutpro/${params.idCutoutproOperation}`),
                fromCutoutPro: (params:Types.Apis.Payloads.Next.Gallery.HeadshotsFromCutoutProRequest): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsFromCutoutProResponse> =>
                    authFetcher.postJson("/next-api/gallery/headshots/fromCutoutpro", params),
                getImages: async ({agentId=undefined}={}): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/headshots` + buildUrlQuery({idAgent: agentId})),
                upload: async (params:Types.Apis.Payloads.Next.Gallery.HeadshotsUploadRequest): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsUploadResponse> =>
                    authFetcher.postMultipart(
                        `/next-api/gallery/headshots` + buildUrlQuery({
                            idAgent: params.agentId,
                        }),
                        params.formData,
                        params.onUploadProgress,
                        params.onDownloadProgress
                    ),
                update: async (params:Types.Apis.Payloads.Next.Gallery.HeadshotsUpdateRequest): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsUpdateResponse> =>
                    authFetcher.postJson(`/next-api/gallery/headshots/${params.id}`, {
                        ...params.postData,
                        ...params.agentId && {
                            idAgent: params.agentId,
                        },
                    }),
                delete: async (id:number, {agentId=undefined}={}): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/headshots/${id}`, {
                        disabled: true,
                        ...agentId && {
                            idAgent: agentId,
                        },
                    }),
                unDelete: async (id:number, {agentId=undefined}={}): Promise<Types.Apis.Payloads.Next.Gallery.HeadshotsUnDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/headshots/${id}`, {
                        disabled: false,
                        ...agentId && {
                            idAgent: agentId,
                        },
                    }),
            },

            designations: {
                getImages: async (idTemplate:number, requestParameters?): Promise<Types.Apis.Payloads.Next.Gallery.DesignationsGetImagesResponse> =>
                    authFetcher.postJson(`/next-api/gallery/designations/${idTemplate}`, requestParameters),
            },

            brandLogos: {
                getEntityImages: async (): Promise<Types.Apis.Payloads.Next.Gallery.LogosGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/all-brand-logos`),
            },

            logos: {
                getImages: async (): Promise<Types.Apis.Payloads.Next.Gallery.LogosGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/logos`),
                upload: async (params:Types.Apis.Payloads.Next.Gallery.LogosUploadRequest): Promise<Types.Apis.Payloads.Next.Gallery.LogosUploadResponse> =>
                    authFetcher.postMultipart(
                        `/next-api/gallery/logos/upload`,
                        params.formData,
                        params.onUploadProgress,
                        params.onDownloadProgress
                    ),
                update: async (params:Types.Apis.Payloads.Next.Gallery.LogosUpdateRequest): Promise<Types.Apis.Payloads.Next.Gallery.LogosUpdateResponse> =>
                    authFetcher.postJson(`/next-api/gallery/logos/update/${params.id}`, {
                        ...params.postData,
                        ...params.agentId && {
                            idAgent: params.agentId,
                        },
                    }),
                delete: async (id:number, {agentId=undefined}={}): Promise<Types.Apis.Payloads.Next.Gallery.LogosDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/logos/delete/${id}`, {
                        disabled: true,
                        ...agentId && {
                            idAgent: agentId,
                        },
                    }),
            },

            orders: {
                getImages: async (orderId:number): Promise<Types.Apis.Payloads.Next.Gallery.OrdersGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/orders/${orderId}`),
                upload: async (params:Types.Apis.Payloads.Next.Gallery.OrdersUploadRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersUploadResponse> => {

                    // post the file direct if under post size limit of if no size limit was provided
                    if (!params.postMaxSize || params.formData.file.size <= params.postMaxSize) {
                        return authFetcher.postMultipart(`/next-api/gallery/orders/${params.orderId}`, params.formData, params.onUploadProgress, params.onDownloadProgress);
                    }

                    // send chunks if over the limit
                    const response = await sendChunkedUpload(params.formData.file, params.onUploadProgress, params.onDownloadProgress);
                    if (!response.fileToken) return null;

                    return authFetcher.postMultipart(`/next-api/gallery/orders/${params.orderId}`, {
                        fileToken: response.fileToken
                    });

                },
                copy: async (params:Types.Apis.Payloads.Next.Gallery.OrdersCopyRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersCopyResponse> =>
                    authFetcher.postJson(`/next-api/gallery/orders/${params.orderId}/${params.imageSource}/${params.imageId}`),
                update: async (params:Types.Apis.Payloads.Next.Gallery.OrdersUpdateRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersUpdateResponse> =>
                    authFetcher.postJson(`/next-api/gallery/orders/${params.orderId}/${params.imageId}`, params.postData),
                delete: async (params:Types.Apis.Payloads.Next.Gallery.OrdersDeleteRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/orders/${params.orderId}/${params.imageId}`, {disabled: true}),
                unDelete: async (params:Types.Apis.Payloads.Next.Gallery.OrdersUnDeleteRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersUnDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/orders/${params.orderId}/${params.imageId}`, {disabled: false}),
                deleteListingImages: async(params:Types.Apis.Payloads.Next.Gallery.OrdersDeleteListingImagesRequest): Promise<Types.Apis.Payloads.Next.Gallery.OrdersDeleteListingImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/orders/${params.orderId}/delete-listing-images`),
            },

            qrCodes: {
                getImages: async (orderId:number): Promise<Types.Apis.Payloads.Next.Gallery.QrCodesGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/qr-codes/${orderId}`),
                upload: async (params:Types.Apis.Payloads.Next.Gallery.QrCodesUploadRequest): Promise<Types.Apis.Payloads.Next.Gallery.QrCodesUploadResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/qr-codes/${params.orderId}`, params.formData, params.onUploadProgress, params.onDownloadProgress),
                update: async (params:Types.Apis.Payloads.Next.Gallery.QrCodesUpdateRequest): Promise<Types.Apis.Payloads.Next.Gallery.QrCodesUpdateResponse> =>
                    authFetcher.postJson(`/next-api/gallery/qr-codes/${params.orderId}/${params.imageId}`, params.postData),
                delete: async (params:Types.Apis.Payloads.Next.Gallery.QrCodesDeleteRequest): Promise<Types.Apis.Payloads.Next.Gallery.QrCodesDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/qr-codes/${params.orderId}/${params.imageId}`, {disabled: true}),
                unDelete: async (params:Types.Apis.Payloads.Next.Gallery.QrCodesUnDeleteRequest): Promise<Types.Apis.Payloads.Next.Gallery.QrCodesUnDeleteResponse> =>
                    authFetcher.postJson(`/next-api/gallery/qr-codes/${params.orderId}/${params.imageId}`, {disabled: false}),
            },

            previousOrders: {
                getOrders: async (): Promise<Types.Apis.Payloads.Next.Gallery.PreviousOrdersGetOrdersResponse> =>
                    authFetcher.getJson(`/next-api/gallery/previous-orders`),
                getImages: async (id:number): Promise<Types.Apis.Payloads.Next.Gallery.PreviousOrdersGetImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/previous-orders/${id}`),
            },

            photoServices: {
                getAvailableServices: async (): Promise<Types.Apis.Payloads.Next.Gallery.PhotoServicesGetAvailableServicesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/photo-services`),
                getServiceAccountFolders: async (serviceId:number, serviceEmail:string, orderId:number): Promise<Types.Apis.Payloads.Next.Gallery.PhotoServicesGetServiceAccountFoldersResponse> =>
                    authFetcher.getJson(`/next-api/gallery/photo-services/${serviceId}/folders?accountEmail=${encodeURIComponent(serviceEmail)}&orderId=${orderId}`),
                getServiceAccountImages: async (serviceId:number, folderId:string, serviceEmail:string, orderId:number): Promise<Types.Apis.Payloads.Next.Gallery.PhotoServicesGetServiceAccountImagesResponse> =>
                    authFetcher.getJson(`/next-api/gallery/photo-services/${serviceId}/folders/${folderId}?accountEmail=${encodeURIComponent(serviceEmail)}&orderId=${orderId}`),
                savePhotoServiceImage: async (serviceId:number, folderId:string, imageId:string, accountEmail:string, remoteUrl:string, caption:string, attributionData:Types.Apis.Models.GalleryImageAttribution|null, orderId:number): Promise<Types.Apis.Payloads.Next.Gallery.PhotoServicesSavePhotoServiceImageResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/photo-services/${serviceId}/${imageId}`, {remoteUrl, orderId, accountEmail, folderId, caption, attributionData : (attributionData ? JSON.stringify(attributionData) : null)}),
            },

            integrations: {
                getAll: async (): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsGetAllResponse> =>
                    authFetcher.getJson(`/next-api/gallery/integrations`),
                getFolders: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsGetFoldersRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsGetFoldersResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/integrations/${params.serviceName}/folders`, diffKeys(params, {serviceName: ""})),
                getImages: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsGetImagesRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsGetImagesResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/integrations/${params.serviceName}/folder/${params.folderId}`, diffKeys(params, {serviceName: "", folderId: ""})),
                searchImages: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsSearchImagesRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsSearchImagesResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/integrations/${params.serviceName}/search`, diffKeys(params, {serviceName: ""})),
                delete: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsDeleteRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsDeleteResponse> =>
                    authFetcher.getJson(`/next-api/gallery/integrations/${params.serviceName}/delete`),
                insert: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsInsertRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsInsertResponse> =>
                    authFetcher.postJson(`/next-api/gallery/integrations/${params.serviceName}/insert`, diffKeys(params, {serviceName: ""})),
                generateToken: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsGenerateTokenRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsGenerateTokenResponse> =>
                    authFetcher.postJson(`/next-api/gallery/integrations/${params.serviceName}/generate-access-token`, diffKeys(params, {serviceName: ""})),
                getAuthToken: async (params:Types.Apis.Payloads.Next.Gallery.IntegrationsGetAuthTokenRequest): Promise<Types.Apis.Payloads.Next.Gallery.IntegrationsGetAuthTokenResponse> =>
                    authFetcher.getJson(`/next-api/gallery/integrations/${params.serviceName}/get-auth-token`),
            },

            listings: {
                saveImage: async (params:Types.Apis.Payloads.Next.Gallery.ListingsSaveImageRequest): Promise<Types.Apis.Payloads.Next.Gallery.ListingsSaveImageResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/listings/${params.listingId}`, params),
                saveImages: async (params:Types.Apis.Payloads.Next.Gallery.ListingsSaveImageRequest): Promise<Types.Apis.Payloads.Next.Gallery.ListingsSaveImageResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/listings/photos/${params.listingId}`, params),
            },

            pdf: {
                validateUpload: async(params:Types.Apis.Payloads.Next.Gallery.PdfValidateUploadRequest): Promise<Types.Apis.Payloads.Next.Gallery.PdfValidateUploadResponse> => {

                    // post the file directly if under post size limit or if no size limit was provided
                    if (!params.postMaxSize || params.formData.file.size <= params.postMaxSize) {
                        return authFetcher.postMultipart(`/next-api/gallery/pdf/validate`, params.formData, params.onUploadProgress, params.onDownloadProgress);
                    }

                    // send in chunks if over the limit
                    const response = await sendChunkedUpload(params.formData.file, params.onUploadProgress, params.onDownloadProgress);
                    if (!response.fileToken) return response;
                    return authFetcher.postMultipart(`/next-api/gallery/pdf/validate`, {
                        templateId: params.formData.templateId,
                        fileToken: response.fileToken,
                    });

                },
                validateUri: async(params:Types.Apis.Payloads.Next.Gallery.PdfValidateUriRequest): Promise<Types.Apis.Payloads.Next.Gallery.PdfValidateUriResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/pdf/validate`, params),
                validateImageAsset: async(params:Types.Apis.Payloads.Next.Gallery.PdfValidateImageAssetRequest): Promise<Types.Apis.Payloads.Next.Gallery.PdfValidateImageAssetResponse> =>
                    authFetcher.postMultipart(`/next-api/gallery/pdf/validate`, params),
                splitPdf: async(params:Types.Apis.Models.PdfValidationData): Promise<Types.Apis.Models.PdfValidationData> =>
                    authFetcher.postJson(`/next-api/gallery/pdf/splitPdf`, params),
            },

        },

        uploads: {
            uploadPart: async (params:Types.Apis.Payloads.Next.Uploads.UploadPartRequest): Promise<Types.Apis.Payloads.Next.Uploads.UploadPartResponse> =>
                authFetcher.postMultipart(`/next-api/uploads/upload-part`, params, params.onUploadProgress, params.onDownloadProgress),
        },

        shipping: {

            ...BaseShippingApis({fetcher: authFetcher}),

            getShippingAddresses: async (id:number) => {
                return authFetcher.getJson(`/next-api/shipping/get_shipping_addresses/${id}`)
            },

            getOfficeShippingAddress: async ({idBranch}:Types.Apis.Payloads.Next.Shipping.GetOfficeShippingAddress.Request = {}): Promise<Types.Apis.Payloads.Next.Shipping.GetOfficeShippingAddress.Response> => {
                const addresses = await authFetcher.getJson(`/next-api/shipping/office_shipping_address${idBranch ? `/${idBranch}` : ""}`);
                return addresses[0];
            },

            // TODO SC flow does not need estimated delivery date data from response. Find a way to disable it to
            // save performance
            getShippingRates: async ({addressId, items, scFlow=false}) => {
                const result = await authFetcher.postJson(`/next-api/shipping/get_shipping_rates/`, {
                    addressId,
                    items: items
                        ?.filter((item) => item.selections.distributionType === "shipping")
                        .map(({sku, shipDate, weight}) => ({sku, shipDate, weight}))
                    ,
                    scFlow
                });
                if (result.errors) {
                    throw result.error_code ?? result.errors[0].msg;
                }
                return result;
            },
            getConsolidatedShippingRates: async ({addressId, cartId, isInternalUser, itemId=undefined}) => {
                const result = await authFetcher.postJson(`/next-api/shipping/get_consolidated_shipping_rates/`, {
                    addressId,
                    cartId,
                    ...isInternalUser && {
                        fedexCharge: true,
                    },
                    itemId
                });
                if (result.errors) {
                    throw result.errors[0].msg ? result.errors[0].msg : result;
                }
                return result;
            },

            getMyShippingAddresses: async (params:Types.Apis.Payloads.Next.Shipping.GetMyShippingAddressesRequest={}): Promise<Types.Apis.Payloads.Next.Shipping.GetMyShippingAddressesResponse> =>
                authFetcher.getJson(`/next-api/shipping/my_shipping_addresses${buildUrlQuery(params)}`),
            addShippingAddress: async ({address, idEntity}:Types.Apis.Payloads.Next.Shipping.AddShippingAddressRequest): Promise<Types.Apis.Payloads.Next.Shipping.AddShippingAddressResponse> =>
                authFetcher.postJson(`/next-api/shipping/add_shipping_address/`, {address, idEntity}),
            updateShippingAddress: async ({id, address}) => {
                return await authFetcher.postJson(`/next-api/shipping/update_shipping_address/`, {id, address});
            },
            deleteShippingAddress: async (id, {agentId=undefined}={}) => {
                return await authFetcher.postJson(`/next-api/shipping/delete_shipping_address/${id}` + buildUrlQuery({idEntity: agentId}));
            },
            getShippingTrackingDetails: async (id) => {
                return await authFetcher.getJson(`/next-api/shipping/tracking/${id}`);
            },
            getShippingTrackingInfo: async (orderId) => {
                return await authFetcher.getJson(`/next-api/shipping/trackingByOrderID/${orderId}`);
            },
        },

        social: {
            ...NextSocialApis({
                share_location: "xpresso",
                fetcher: authFetcher,
                host: undefined,
            }),

            getAllPosts: async ({idEntity}) => {
                return await authFetcher.getJson(`/next-api/social/getAllPosts` + (idEntity ? `/${idEntity}` : ''))
            },
            getPostMetrics: async ({ids}) => {
                return await authFetcher.getJson(`/next-api/social/getPostMetrics?ids=${ids}`)
            },
            getFacebookAdsStats: async ({itemId}) => {
                return await authFetcher.getJson(`/next-api/facebookAds/getFacebookAdsStats/${itemId}`)
            },
        },

        subscription: {
            create: async ({planId, referenceUrl}) => {
                return await authFetcher.postJson(`/next-api/subscriptions/createChargebeeSubscriptions` + buildUrlQuery({
                    planId,
                    referenceUrl,
                }))
            },
            getDetails: async ({idAgent}) => {
                return await authFetcher.getJson(`/next-api/subscriptions/get/${idAgent}/0`)
            },
            getPortal: async () => {
                return await authFetcher.getJson(`/next-api/subscriptions/createPortalSession`)
            },
            getSubscriptionPlans: async () => {
                return await authFetcher.getJson(`/next-api/subscriptions/getSubscriptionPlans`)
            },
            sync: async () => {
                return await authFetcher.getJson(`/next-api/subscriptions/syncChargebeeSubscriptions`)
            },
            getPlanUpdateOptions: async ({idAgent}:{idAgent?: string}={}): Promise<Types.Apis.Payloads.Next.Subscriptions.GetPlanUpdateOptions.Response> => {
                return await authFetcher.getJson(`/next-api/subscriptions/planUpdateOptions?${buildUrlQuery({idAgent})}`)
            },
            updatePlan: async ({idPricePoint, idAgent}) => {
                return await authFetcher.postJson(`/next-api/subscriptions/updatePlan`, {idPricePoint, idAgent})
            },
            cancelScheduledUpdate: async (idAgent=null) => {
                return await authFetcher.postJson(`/next-api/subscriptions/cancelScheduledUpdate`, {idAgent})
            },
            getActiveSubscriptionCreditCard: async ({idAgent}) => {
                return await authFetcher.getJson(`/next-api/subscriptions/activeCreditCard` + buildUrlQuery({idAgent}))
            },
        },

        billing: {
            getBillingAccounts: async ({idProgram, idBranch, idAgent, idRegion}={}) => {
                return await authFetcher.getJson(`/next-api/billing/get_billing_accounts` + buildUrlQuery({
                    idProgram,
                    idBranch,
                    idAgent,
                    idRegion,
                }))
            },
        },

        cart: CartApis({authFetcher}),

        creditCard: {
            getCreditCards: async ({idAgent=null}={}) => {
                const gross = await authFetcher.getJson(`/next-api/creditcard/get_credit_cards` + (idAgent == null ? "" : "/" + idAgent));
                const cards = gross && gross.creditCards;
                return cards == null ? (
                    []
                ) : Array.isArray(cards) ? (
                    cards
                ) : (
                    [cards]
                );
            },
            getAuthorizationToken: async ({recaptchaToken}) => {
                const [{authorizationToken}] = await authFetcher.postJson(
                    `/next-api/creditcard/get_authorization_token`,
                    {recaptchaToken},
                );
                return authorizationToken;
            },
            addUpdate: async (req, agentId = null) => {
                const result = await authFetcher.postJson(`/next-api/creditcard/add_update_credit_card`, {
                    ...req,
                    ...agentId != null && {
                        idEntity: agentId,
                    },
                });
                if (result?.errors) {
                    throw result.errors[0].errorMessage;
                }
                return result;
            },
            deleteCard: async (cardToken, agentId=null) => {
                return await authFetcher.postJson(`/next-api/creditcard/delete_card`, {
                    cardToken,
                    ...agentId != null && {
                        idEntity: agentId,
                    }
                });
            },
            makeCardDefault: async (cardToken) => {
                return await authFetcher.postJson(`/next-api/creditcard/make_card_default`, {cardToken});
            },
        },

        mailing: MailingApis({fetcher: authFetcher}),

        getTemplateOptions: async (sku): Promise<Types.Apis.Payloads.Next.TemplateOptions.Details.Response> => {
            if (sku.front) {
                sku.front = +sku.front;
            }
            if (sku.back) {
                sku.back = +sku.back;
            }
            const response = await authFetcher.postJson(
                "/next-api/templateOptions/details",
                {sku}
            );
            if (response?.error) return null;
            return response;
        },

        getTemplateVariations: (
            idTemplate:number
        ): Promise<Types.Apis.Payloads.Next.Template.GetVariations.Response> => (
            authFetcher.getJson(`/next-api/template/getVariations/${idTemplate}`)
        ),

        vouchers: {
            getVoucherHistory: async () => {
                const result = await authFetcher.getJson("/next-api/voucher/get_voucher_history/");
                if (equalDeep(result, {"result":"failure","errors":{"target":"getVouchersHistoryByIdAgent","msg":"No vouchers were found that matched your search criteria."}})) {
                    return {};
                }
                return result;
            },

            createVoucherCode: async (voucherData) => {
                return await authFetcher.postJson("/next-api/voucher/create_voucher", voucherData);
            },
            updateAgentVoucher: async (voucherData) => {
                return await authFetcher.postJson("/next-api/voucher/update_agent_voucher", voucherData);
            },

            getVouchers1: async (
                {
                    search, // “Last Name” or “First Name” or “Agent id”
                    limit=undefined, // Default value : 9999
                    offset=undefined, // Default value : 0
                    sortby=undefined, // Sorting by “Name” or “Program” or “Amount” or “ExpirationDate” or “LastModified"
                    sortorder=undefined, // Sorting Order “asc” or “desc”
                }
            ) => {
                return await authFetcher.getJson("/next-api/voucher/get_agent_vouchers" + buildUrlQuery({
                    search,
                    limit,
                    offset,
                    sortby,
                    sortorder,
                }));
            },
            getVoucherHistory1: async (voucherId) => {
                return chain(
                    await authFetcher.getJson("/next-api/voucher/get_history_voucher/" + buildUrlQuery({idvoucher: voucherId})),
                    (_) => values(_)[0],
                );
            },
        },

        adBudgets: {
            getUserAdBudget: async ({idBranch, search}) => {
                const result = await authFetcher.getJson("/next-api/adBudget/get_user_adBudget" + buildUrlQuery({
                    idBranch,
                    search,
                }));
                if (equalDeep(result, {"result":"failure","errors":{"target":"getUserAdBudget","msg":"No adBudgets were found that matched your search criteria."}})) {
                    return {};
                }
                return result;
            },
            setUserAdBudget: async (data) => {
                const result = await authFetcher.postJson("/next-api/adBudget/set_user_adBudget", data);
                if (equalDeep(result, {"result":"failure","errors":{"target":"setUserAdBudget","msg":"Error when trying to set adBudgets."}})) {
                    return {};
                }
                return result;
            },
            getAdBudgetHistory: async () => {
                const result = await authFetcher.getJson("/next-api/adBudget/get_user_adBudget_history/");
                if (equalDeep(result, {"result":"failure","errors":{"target":"getAdBudgetHistory","msg":"No adBudgets were found that matched your search criteria."}})) {
                    return {};
                }
                return result;
            },
            getAdBudgetDefaults: async (idBranch) => {
                const result = await authFetcher.postJson("/next-api/adBudget/get_adBudget_defaults", idBranch);
                if (equalDeep(result, {"result":"failure","errors":{"target":"getAdBudgetDefaults","msg":"No adBudgets were found that matched your search criteria."}})) {
                    return {};
                }
                return result;
            },
            setAdBudgetDefaults: async (branches) => {
                const result = await authFetcher.postJson("/next-api/adBudget/set_adBudget_defaults", branches);
                if (equalDeep(result, {"result":"failure","errors":{"target":"setAdBudgetDefaults","msg":"Error when trying to set adBudgets."}})) {
                    return {};
                }
                return result;
            },
        },

        approval: {
            getApprovalRequests: async (idBranch:string=null, showOfficeApprovals:string=null): Promise<Types.Apis.Payloads.Next.OrderApproval.GetApprovalRequests.Response> => (
                authFetcher.getJson("/next-api/orderApproval/getApprovalRequests" + buildUrlQuery({
                    idBranch,
                    showOfficeApprovals,
                }))
            ),
            getPendingRequests: async (): Promise<Types.Apis.Payloads.Next.OrderApproval.GetAllApprovalRequests.Response> => (
                authFetcher.getJson("/next-api/orderApproval/getApprovalRequests/all")
            ),
            denyCcCart: async (cartId:number) => authFetcher.postJson("/next-api/orderApproval/deny", {cartId}),
            denyLegacyOrder: async (itemId:number) => authFetcher.postJson("/next-api/orderApproval/deny", {itemId}),
            denyCcOrder: async (ids:{cartId?: number, itemId?: number}) => authFetcher.postJson("/next-api/orderApproval/deny", ids),
            denyProofOrder: async(ids:{cartId?: number, itemId?: number}) => authFetcher.postJson("/next-api/orderApproval/deny", ids),

            approveCcCart: (cartId) => authFetcher.postJson("/next-api/orderApproval/approve", {cartId: ""+cartId}),
            approveLegacyOrder: (orderId) => authFetcher.postJson("/next-api/orderApproval/submitOrder", {orderId: ""+orderId}),
            getOciForm: (cartId) => authFetcher.postJson(
                "/next-api/orderApproval/getOciForm",
                {cartId: ""+cartId},
            ),
            processProcurement: (cartId) => authFetcher.postJson(
                "/next-api/orderApproval/processProcurement",
                {cartId: ""+cartId},
            ),
        },

        bulkMailPermit: {
            getIndica: async ({agentId=undefined, userLevel=undefined}={}) => {
                const result = await authFetcher.getJson("/next-api/bulkmailpermit/userIndicia" + buildUrlQuery({
                    ...agentId && {
                        entityId: agentId,
                        userLevel: userLevel ?? AGENT_LEVEL,
                    },
                }));
                return result.idPrimary == null ? null : result;
            },
            saveIndicia: async ({id, indicia, lines, agentId=undefined, userLevel=undefined}:{
                id      ?: number,
                indicia ?: Record<"line1" | "line2" | "line3" | "line4" | "line5", string>,
                lines   ?: [string, string, string, string, string],
                agentId ?: string,
                userLevel ?: number,
            }) => {
                const result = await authFetcher.postJson("/next-api/bulkmailpermit/saveUserIndicia/", {
                    id,
                    lines: indicia ? [indicia.line1, indicia.line2, indicia.line3, indicia.line4, indicia.line5] : lines,
                    ...agentId && {
                        entityId: agentId,
                        userLevel: userLevel ?? AGENT_LEVEL,
                    },
                });
                if (result?.error) {
                    throw result.error.msg;
                }
                return result;
            },
        },


        imb: {
            getMailerId: async ({agentId=undefined}={}) => {
                const result = await authFetcher.getJson("/next-api/mailer/getMailerId" + buildUrlQuery({idEntity: agentId}));
                if (result.result === "failure" && result.error?.msg === "You do not have a Mailer ID.") {
                    // {
                    //     result: "failure",
                    //     error: {
                    //         target: "getMailerId",
                    //         msg: "You do not have a Mailer ID.",
                    //     },
                    // };

                    return null;
                }
                return result;
            },

            saveMailerId: async ({idMailer, idPrimary, idEntity=undefined}) => {
                const result = await authFetcher.postJson("/next-api/mailer/save_mailer_id/", {
                    "mailerId": idMailer,
                    "idEntity": idEntity ?? "",
                    "mailerIdPrimary": idPrimary ?? 0
                });

                // {"result":"failure","error":{"target":"saveMailerId","msg":"The Mailer ID you provided is invalid. Values must be numeric and between 6 to 9 characters in length."}}
                if (result.result === "failure") {
                    throw result.error?.msg ?? "Failed saving mailer id";
                }

                // {"error":{"fatal":"1","errors":[{"target":"validateIdEntity","msg":"agent level not have access another mailer ids"}]}}
                if (result.error) {
                    throw result.error.errors?.[0]?.msg ?? "Failed saving mailer id";
                }
                return result;
            },
        },

        userProfile: {
            getUserProfile: async (userId?: number): Promise<Types.Apis.Payloads.Next.UserProfile.GetUser.Response> => (
                await authFetcher.getJson(`/next-api/userProfile/get${userId ? '/' + userId : ''}`)
            ),
            updateUserProfile: async ({userId=undefined,...updateFields}:Types.Apis.Payloads.Next.UserProfile.UpdateUser.Request): Promise<Types.Apis.Payloads.Next.UserProfile.UpdateUser.Response> => (
                await authFetcher.postJson(`/next-api/userProfile/update`, userId ? {userId, ...updateFields} : updateFields)
            ),
        },

        sampleAddress: SampleAddressApis({fetcher: authFetcher}),

        program: {
            getList: ():Promise<Types.Apis.Payloads.Next.Program.GetList.Response> => (
                authFetcher.getJson("/next-api/program/list/0")
            ),
            getProgramIds: ():Promise<Types.Apis.Payloads.Next.Program.GetList.Response> => (
                authFetcher.getJson("/next-api/program/id_list/0")
            ),
        },

        region: {
            getRegionList: ():Promise<Types.Apis.Payloads.Next.Region.GetList.Response> => (
                authFetcher.getJson("/next-api/region/list", null, false)
                    .then((res:Response) => {
                        if (res.status === 404) {
                            return [];
                        } else if (res.status === 200) {
                            return res.json();
                        } else {
                            return Promise.reject(res);
                        }
                    })
            ),
        },

        reporting: {
            deliveryTypeReport: (
                params:Types.Apis.Payloads.Next.Reporting.DeliveryTypeReport.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.DeliveryTypeReport.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/deliveryTypeReport", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.DeliveryTypeReport.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.DeliveryTypeReport.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            topTenOffices: (
                params:Types.Apis.Payloads.Next.Reporting.TopTenOffices.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.TopTenOffices.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/topTenOffices", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TopTenOffices.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TopTenOffices.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            topTenAgents: (
                params:Types.Apis.Payloads.Next.Reporting.TopTenAgents.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.TopTenAgents.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/topTenAgents", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TopTenAgents.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TopTenAgents.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            totalSpend: (params:Types.Apis.Payloads.Next.Reporting.TotalSpend.Request={}): Promise<Types.Apis.Payloads.Next.Reporting.TotalSpend.Response> => (
                authFetcher.postJson("/next-api/reporting/totalSpend", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TotalSpend.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success" || (body as Types.Apis.Payloads.Next.Reporting.ApiErrorResponse).message) {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TotalSpend.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            totalSpendByProduct: (
                params:Types.Apis.Payloads.Next.Reporting.TotalSpendByProduct.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.TotalSpendByProduct.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/totalSpendByProduct", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TotalSpendByProduct.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TotalSpendByProduct.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            totalSpendByProgram: (
                params:Types.Apis.Payloads.Next.Reporting.TotalSpendByProgram.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.TotalSpendByProgram.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/totalSpendByProgram", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TotalSpendByProgram.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TotalSpendByProgram.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            transactionDetailReport: (params:Types.Apis.Payloads.Next.Reporting.Transaction.Request): Promise<void> => (
                authFetcher.postJson("/next-api/reporting/transaction", params, {returnJson: false})
                    .then((res:Response) => {
                        if (res.status >= 400)
                            return Promise.reject(res);
                    })
            ),
            transactionInformation: (params:Types.Apis.Payloads.Next.Reporting.TransactionInformation.Request): (
                Promise<Types.Apis.Payloads.Next.Reporting.TransactionInformation.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/transactionInformation", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.TransactionInformation.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.TransactionInformation.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
            userRegistrationReport: (
                params:Types.Apis.Payloads.Next.Reporting.UserRegistrationReport.Request={}
            ): (
                Promise<Types.Apis.Payloads.Next.Reporting.UserRegistrationReport.Response>
            ) => (
                authFetcher.postJson("/next-api/reporting/userRegistrationReport", params, {returnJson: false})
                    .then(async (res:Response) => {
                        const body:Types.Apis.Payloads.Next.Reporting.UserRegistrationReport.ApiResponse = await res.json();
                        if (res.status >= 400 || body.statusCode !== "success") {
                            throw {body, res};
                        }
                        return omit(body as Types.Apis.Payloads.Next.Reporting.UserRegistrationReport.ApiSuccessResponse, ["statusCode"]);
                    })
            ),
        },

        userCreated: {
            getFolders: ( onlyAllowed=0, filterType="" ) => authFetcher.getJson(`/next-api/user-created/getFolders/${onlyAllowed}` + (filterType ? `/${filterType}` : '')),
            getFolder: (idFolder) => authFetcher.getJson(`/next-api/user-created/getFolder/${idFolder}`),
            deleteFolder: (idFolder) => authFetcher.deleteJson(`/next-api/user-created/deleteFolder/${idFolder}`),
            updateFolder: async ({updateFields}) => await authFetcher.postJson("/next-api/user-created/updateFolder", updateFields),
            updateFolderSequence: async (updateFields) => await authFetcher.postJson("/next-api/user-created/updateFolderSequence", updateFields),
            getProducts: () => authFetcher.getJson(`/next-api/user-created/getProducts`),
            getTemplates: (idFolder, onlyAllowed=0, deleted=0) => authFetcher.getJson(`/next-api/user-created/getTemplates/${idFolder}/${onlyAllowed}/${deleted}`
                + ["listingId", "listingRequestType"]
                .map((k) => {
                    const value = sessionStorage.getItem(k);
                    return value && "/" + encodeURIComponent(value);
                })
                .filter(v => v)
                .join("")
            ),
            getTemplate: (idTemplate) => authFetcher.getJson(`/next-api/user-created/getTemplate/${idTemplate}`),
            deleteTemplate: async (deleteParams) => await authFetcher.postJson("/next-api/user-created/deleteTemplate", deleteParams),
            uploadImage: async (requestParameters) => await authFetcher.postJson( `/next-api/user-created/uploadImage`, requestParameters),
            updateTemplate: async ({updateFields}) => await authFetcher.postJson("/next-api/user-created/updateTemplate", updateFields),
            updateTemplateSequence: async (updateFields) => await authFetcher.postJson("/next-api/user-created/updateTemplateSequence", updateFields),
            updateLog: async ({updateFields}) => await authFetcher.postJson("/next-api/user-created/updateLog", updateFields),
        },

        gpt: {
            textCompletion: (requestParameters) => authFetcher.postJson("/next-api/gpt/text-completion", requestParameters),
            chatCompletion: (requestParameters) => authFetcher.postJson("/next-api/gpt/chat-completion", requestParameters),
            autoGeneratedText: (userCueData) => authFetcher.postJson("/next-api/gpt/auto-generated-text", userCueData),
            aiSummarize: (requestParameters) => authFetcher.postJson("/next-api/gpt/ai-summarize", requestParameters),
            aiExpand: (requestParameters) => authFetcher.postJson("/next-api/gpt/ai-expand", requestParameters),
            aiReword: (requestParameters) => authFetcher.postJson("/next-api/gpt/ai-reword", requestParameters),
            logAiInteraction: (requestParameters) => authFetcher.postJson("/next-api/gpt/ai-logging", requestParameters),
            aiTextFromImage: (requestParameters) => authFetcher.postJson("/next-api/gpt/ai-text-from-image", requestParameters),
            getUserAiImageToolUses: (requestParameters) => authFetcher.postJson("/next-api/gpt/get-users-remaining-ai-actions", requestParameters),
            updateRemainingAiActions: (requestParameters) => authFetcher.postJson("/next-api/gpt/update-remaining-ai-actions", requestParameters),
            getPropertyWizardData: (idOrder) => authFetcher.getJson(`/next-api/gpt/get-property-wizard-data/${idOrder}`),
            savePropertyWizardData: (requestParameters) => authFetcher.postJson("/next-api/gpt/save-property-wizard-data", requestParameters),
            createPropertyAgregateSummary: (requestParameters) => authFetcher.postJson("/next-api/gpt/property-aggregate-desc", requestParameters),
            generateFieldSuggestions: (requestParameters) => authFetcher.postJson("/next-api/gpt/property-field-suggestions", requestParameters),
        },

        monthlyMarketData: {
            monthlyMarketData: {getMonthlyMarketData: (zipCode) => authFetcher.getJson(`/next-api/monthly-market-data/get-market-data-by-zip,/${zipCode}`)},

        },

        bugBounty: {
            getBugBountyProgramData: () => authFetcher.getJson("/next-api/bug-bounty/get-bug-bounty-program-data"),
            updateBugBountyProgramData: (newBugBountyData) => authFetcher.postJson("/next-api/bug-bounty/update-bug-bounty-program-data", newBugBountyData),
        },

        helpMgmt: {
            getHelpMgmt: () => authFetcher.getJson(`/next-api/helpmgmt/getHelpMgmt`),
        },

        mockData: {
            getMockData: () => authFetcher.getJson(`/next-api/mock-data/get-mock-data`)
        },

        authenticate: {
            agentConfirm: ({idAgent}) => authFetcher.postJson('/next-api/authenticate/agentConfirm', {
                idAgent
            }),
            agentNew: () => authFetcher.getJson('/next-api/authenticate/agentNew'),
            agentNewSignup: async ({createFields}) => authFetcher.postJson('/next-api/authenticate/agentNewSignup', createFields),
            createGuestAgent: async (idBranch=null) => {
                return await authFetcher.postJson(`/next-api/authenticate/createGuestAgent`, {idBranch})
            },
            data: () => authFetcher.getJson('/next-api/authenticate/data'),
            error: () => authFetcher.getJson('/next-api/authenticate/error'),
            test: () => authFetcher.getJson('/next-api/authenticate/test'),
            getUserNames: (request:Types.Apis.Payloads.Next.Authenticate.GetUserNames.Request):
                Promise<Types.Apis.Payloads.Next.Authenticate.GetUserNames.Response> => (
                authFetcher.postJson("/next-api/authenticate/get_user_names", request)
            ),
            login: (body:{username, password, captcha, existingAccount}) =>
                authFetcher.postJson('/next-api/authenticate/login', body),
            officeSelection: ({idOffice, newUser}:{idOffice:string, newUser:boolean}) => authFetcher.postJson('/next-api/authenticate/officeSelection', {
                idOffice,
                newUser
            }),
            quickLogin: ({idUser}:{idUser:number}) => authFetcher.postJson('/next-api/authenticate/quickLogin', {
                idUser
            }),
            sendLoginEmail: (request:Types.Apis.Payloads.Next.Authenticate.SendLoginEmail.Request): Promise<any> => (
                authFetcher.postJson("/next-api/authenticate/send_login_email", request)
            ),
            googleLogin: ({token}:{token:string}) => authFetcher.postJson('/next-api/authenticate/googleLogin', {
                token
            }),
            facebookLogin: ({token}:{token:string}) => authFetcher.postJson('/next-api/authenticate/facebookLogin', {
                token
            }),
            startGuestSession: async (idCompany=null) => {
                return await authFetcher.postJson(`/next-api/authenticate/startGuestSession`, {idCompany})
            },
            validateXpressoToken: ({xpressoToken}) => {
                return authFetcher.postJson("/next-api/authenticate/token", {"token": "" + xpressoToken});
            },
        },
        templateAdmin: {
            getVariableDataMergeFields: async (): Promise<Types.Apis.Payloads.Next.TemplateAdmin.VariableDataMergeFieldsResponse> =>
                authFetcher.getJson("/next-api/templateAdmin/variableDataMergeFields"),
        },
        analytics: {
            leadInteractions: (idAgent) => authFetcher.getJson(`/next-api/analytics/leadInteractions/${idAgent}`),
        },
        myNeighborhood: {
            getHomeValue: async (data) => await authFetcher.getJson(
                `/next-api/my-neighborhood/getHomeValue` + buildUrlQuery(data)
            ),
            getXpresslinks: async (data) => await authFetcher.getJson(
                `/next-api/my-neighborhood/getXpresslinks` + buildUrlQuery(data)
            ),
            saveXpresslinks: async (data) => await authFetcher.postJson(
                `/next-api/my-neighborhood/saveXpresslinks`,
                data
            ),
            submitContactForm: async (data) => await authFetcher.postJson(
                `/next-api/my-neighborhood/submitContactForm`,
                data
            ),
        },
        informedDelivery: nextInformedDeliveryApis({fetcher: authFetcher}),

        networking: nextNetworkingApis({fetcher: authFetcher}),

        omniSearch: {
            addSearchHistoryRecord: async ({record}:{
                record: {
                    "action": "search" | "click",
                    "date_created": string,
                    "has_results"?: boolean,
                    "id_agent"?: string,
                    "id_branch"?: string,
                    "id_company": string,
                    "id_user": number,
                    "search_handler": string,
                    "search_term": string,
                    "suggestion_name": string,
                    "suggestion_type": "template" | "none" | "search",
                    "suggestion_data": string,
                }
            }) => await authFetcher.postJson(
                `/next-api/omniSearch/solr/add`,
                {
                    idCollection: 5,
                    'records': [record]
                },
            ),

            getSearchSuggestions: (
                {q}:Types.Apis.Payloads.Next.OmniSearch.GetSearchSuggestions.Request
            ): Promise<Types.Apis.Payloads.Next.OmniSearch.GetSearchSuggestions.Response> => (
                authFetcher.getJson(`/next-api/omniSearch/search/suggestions?${new URLSearchParams({q})}`)
            ),
        },
    });
};

